import type {
  ProjectStatsQueryDTO,
  ProjectStatsResult,
} from "@/modules/management/project/domain/repo/get-project-stats";
import type {
  CreateProjectBody,
  CreateRiskBody,
  CreateTaskBody,
  ListProjectsQuery,
  ListProjectsResult,
  ProjectDetail,
  ProjectRiskListItem,
  ProjectTaskListItem,
  UpdateProjectBody,
  UpdateRiskBody,
  UpdateTaskBody,
} from "@/modules/management/project/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

const base = `${config.apiUrl}/management/projects`;

function buildQueryString(params: ListProjectsQuery): string {
  const search = new URLSearchParams();
  if (params.offset != null) search.set("offset", String(params.offset));
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.sort != null && params.sort.length > 0)
    search.set("sort", JSON.stringify(params.sort));
  if (params.status != null) search.set("status", params.status);
  if (params.level != null) search.set("level", params.level);
  if (params.departmentId != null)
    search.set("departmentId", params.departmentId);
  if (params.positionId != null) search.set("positionId", params.positionId);
  if (params.responsibleUserId != null)
    search.set("responsibleUserId", params.responsibleUserId);
  if (params.startDateFrom != null)
    search.set("startDateFrom", params.startDateFrom);
  if (params.startDateTo != null) search.set("startDateTo", params.startDateTo);
  if (params.endDateFrom != null) search.set("endDateFrom", params.endDateFrom);
  if (params.endDateTo != null) search.set("endDateTo", params.endDateTo);
  if (params.search != null) search.set("search", params.search);
  const q = search.toString();
  return q ? `?${q}` : "";
}

function formValuesToBody(values: {
  name: string;
  description?: string;
  startDate: string;
  endDate: string;
  responsibleUserId?: string;
  level?: "position" | "department" | "organization";
  positionId?: string | null;
  departmentId?: string | null;
  phases: Array<{
    id: string;
    name: string;
    dueDate?: string;
    assigneeId?: string;
  }>;
}): CreateProjectBody {
  return {
    name: values.name,
    description: values.description ?? null,
    startDate: values.startDate,
    endDate: values.endDate,
    responsibleUserId: values.responsibleUserId || null,
    level: values.level ?? "organization",
    positionId: values.positionId?.trim() || null,
    departmentId: values.departmentId?.trim() || null,
    phases: values.phases.map((p) => ({
      id: p.id,
      name: p.name,
      dueDate: p.dueDate ?? null,
      assigneeId: p.assigneeId?.trim() || null,
    })),
  };
}

export const managementProjectApi = {
  list(query: ListProjectsQuery): Promise<ListProjectsResult> {
    return fetcher.get<ListProjectsResult>(`${base}${buildQueryString(query)}`);
  },

  getStats(query: ProjectStatsQueryDTO): Promise<ProjectStatsResult> {
    const url = new URL(`${base}/stats`);
    if (query.departmentId)
      url.searchParams.set("departmentId", query.departmentId);
    if (query.startDateFrom)
      url.searchParams.set("startDateFrom", query.startDateFrom);
    if (query.startDateTo)
      url.searchParams.set("startDateTo", query.startDateTo);
    if (query.level) url.searchParams.set("level", query.level);
    return fetcher.get<ProjectStatsResult>(url.toString());
  },

  getById(id: string): Promise<ProjectDetail> {
    return fetcher.get<ProjectDetail>(`${base}/${encodeURIComponent(id)}`);
  },

  create(body: CreateProjectBody): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(base, body);
  },

  update(id: string, body: UpdateProjectBody): Promise<{ ok: boolean }> {
    return fetcher.put<{ ok: boolean }>(
      `${base}/${encodeURIComponent(id)}`,
      body,
    );
  },

  formValuesToBody,

  getTasks(projectId: string): Promise<ProjectTaskListItem[]> {
    return fetcher.get<ProjectTaskListItem[]>(
      `${base}/${encodeURIComponent(projectId)}/tasks`,
    );
  },

  createTask(projectId: string, body: CreateTaskBody): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(
      `${base}/${encodeURIComponent(projectId)}/tasks`,
      body,
    );
  },

  updateTask(
    projectId: string,
    taskId: string,
    body: UpdateTaskBody,
  ): Promise<{ ok: boolean }> {
    return fetcher.put<{ ok: boolean }>(
      `${base}/${encodeURIComponent(projectId)}/tasks/${encodeURIComponent(taskId)}`,
      body,
    );
  },

  getRisks(projectId: string): Promise<ProjectRiskListItem[]> {
    return fetcher.get<ProjectRiskListItem[]>(
      `${base}/${encodeURIComponent(projectId)}/risks`,
    );
  },

  createRisk(projectId: string, body: CreateRiskBody): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(
      `${base}/${encodeURIComponent(projectId)}/risks`,
      body,
    );
  },

  updateRisk(
    projectId: string,
    riskId: string,
    body: UpdateRiskBody,
  ): Promise<{ ok: boolean }> {
    return fetcher.put<{ ok: boolean }>(
      `${base}/${encodeURIComponent(projectId)}/risks/${encodeURIComponent(riskId)}`,
      body,
    );
  },

  deleteTask(projectId: string, taskId: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(
      `${base}/${encodeURIComponent(projectId)}/tasks/${encodeURIComponent(taskId)}`,
    );
  },

  deleteRisk(projectId: string, riskId: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(
      `${base}/${encodeURIComponent(projectId)}/risks/${encodeURIComponent(riskId)}`,
    );
  },

  deleteProject(id: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(`${base}/${encodeURIComponent(id)}`);
  },
};
